import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Form, { Validations } from '../../../../../components/Form';
import useRequest from '../../../../../hooks/useRequest';
import { ControlItem } from '../../../types';
import { formatParam } from '../../helpers';

import ServicePaths from '../../../../../bridge/utils/ServicePaths';

const ManagePasswordForm = () => {
  const [userId, setUserId] = useState<string | undefined>();
  const [password, setPassword] = useState<string | undefined>();
  const [errors, setErrors] = useState<Record<string, Validations>>({});
  const [canFetchUser, setCanFetchUser] = useState(false);
  const [formMessage, setFormMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const params = useMemo(() => ({ userId }), [userId]);
  const options = useMemo(
    () => ({
      shouldFetch: canFetchUser,
    }),
    [canFetchUser],
  );

  const { data: userData } = useRequest(
    ServicePaths.CONTROL.GetUser,
    params,
    options,
  );

  useEffect(() => {
    if (userData) {
      setIsLoading(false);
    }
  }, [userData]);

  const checkUserIdValidation = useCallback(() => {
    /** userId can not be empty */

    setErrors({});

    if (typeof userId === 'undefined' || userId?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        userId: Validations.Required,
      }));
    }

    return true;
  }, [userId]);

  const checkPasswordValidation = useCallback(() => {
    /** password can not be empty */

    setErrors({});

    if (typeof password === 'undefined' || password?.length === 0) {
      setErrors((prev) => ({
        ...prev,
        password: Validations.Required,
      }));
    }

    return true;
  }, [password]);

  const handleGetUserSubmit = useCallback(() => {
    setIsLoading(true);
    const canContinue = checkUserIdValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setCanFetchUser(true);
  }, [checkUserIdValidation]);

  const handleSetPasswordSubmit = useCallback(async () => {
    setIsLoading(true);
    const canContinue = checkPasswordValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    const { data } = await axios({
      method: 'POST',
      url: ServicePaths.CONTROL.SetPassword,
      params: {
        userId: formatParam(userId),
        password: formatParam(password),
      },
    });

    setIsLoading(false);
    setFormMessage(data);
  }, [checkPasswordValidation, password, userId]);

  const handleRemovePasswordSubmit = useCallback(async () => {
    setIsLoading(true);
    const canContinue = checkPasswordValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    const { data } = await axios({
      method: 'POST',
      url: ServicePaths.CONTROL.RemovePassword,
      params: {
        userId: formatParam(userId),
        password: formatParam(password),
      },
    });

    setIsLoading(false);
    setFormMessage(data);
  }, [checkPasswordValidation, password, userId]);

  const getUserDataConfig = useMemo(() => {
    return {
      title: ControlItem.ManagePassword,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
        },
      ],
      handleSubmit: handleGetUserSubmit,
      isLoading,
    };
  }, [errors.userId, handleGetUserSubmit, isLoading, userId]);

  const noPasswordFormConfig = useMemo(() => {
    return {
      title: 'Add Password',
      formMessage,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
          isLocked: true,
        },
        {
          label: 'Password',
          value: password,
          handleValueChange: setPassword,
          error: errors.password,
          isSecret: true,
        },
      ],
      handleSubmit: handleSetPasswordSubmit,
      isLoading,
    };
  }, [
    errors.password,
    errors.userId,
    formMessage,
    handleSetPasswordSubmit,
    isLoading,
    password,
    userId,
  ]);

  if (!userData) {
    return <Form config={getUserDataConfig} />;
  }

  return <Form config={noPasswordFormConfig} />;
};

export default ManagePasswordForm;
